import { ApplicationConfig, importProvidersFrom, inject, provideAppInitializer } from '@angular/core';
import { provideRouter } from '@angular/router';
import { routes } from './app.routing';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { all } from 'deepmerge';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { ConfigurationService } from './osc-utils/services/configuration.service';
import { OneTrustService } from './osc-utils/services/one-trust.service';
import { APP_BASE_HREF, PlatformLocation } from '@angular/common';
import { ErrorStateMatcher, ShowOnDirtyErrorStateMatcher } from '@angular/material/core';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { getEnvironment } from 'src/shared/helpers/environment.helper';
import { environment } from 'src/environments/environment';
import { ApiInterceptor } from './osc-utils/interceptors/api-interceptor';
import { ToastrModule } from 'ngx-toastr';
import { getAuthProviders, PlatformService, provideEnv } from '@evc/platform';
import { EvcAuthService } from './core/services/evc-auth.service';
import { OscReferenceAppCommonModule } from './osc-reference-app-common/osc-reference-app-common.module';
import { AuthInterceptor } from './core/interceptors/auth.interceptor';
import { provideAnimations } from '@angular/platform-browser/animations';

export function createTranslateLoader() {
  return new TranslateHttpLoader();
}

export const init = (configService: ConfigurationService, oneTrustService: OneTrustService, evcAuthService: EvcAuthService) => () => {
  if (!environment.USE_CONNECT_PLATFORM) {
    oneTrustService.init();
    return configService.load('./endpoints-config/endpoints-config.json');
  }
};

export const httpLoaderFactory = (http: HttpClient): CustomTranslateHttpLoader =>
  CustomTranslateHttpLoader.CustomHttpLoaderFactory(http, [
    { prefix: 'assets/i18n/osc-main-app/', suffix: '.json' },
    { prefix: 'assets/i18n/osc-oil-and-gas/', suffix: '.json' },
    { prefix: 'assets/i18n/osc-purchase/', suffix: '.json' },
    { prefix: 'assets/i18n/osc-reference-app/', suffix: '.json' },
    { prefix: 'assets/i18n/', suffix: '.json' },
  ]);

export const appConfig: ApplicationConfig = {
  providers: [
    provideEnv(environment),
    provideRouter(routes),
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimations(),
    ...TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: httpLoaderFactory,
        deps: [HttpClient],
      },
    }).providers,
    { provide: ErrorStateMatcher, useClass: ShowOnDirtyErrorStateMatcher },
    { provide: APP_BASE_HREF, useFactory: getBaseHref, deps: [PlatformLocation] },
    { provide: HTTP_INTERCEPTORS, useClass: ApiInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: 'OSC_ENVIRONMENT', useFactory: getEnvironment },
    { provide: 'CALLBACK_URL', useValue: environment.baseUrl },
    importProvidersFrom(
      ToastrModule.forRoot({
        maxOpened: 3,
        progressBar: true,
        timeOut: 5000,
        positionClass: 'toast-top-right',
        extendedTimeOut: 500,
      }),
      OscReferenceAppCommonModule,
    ),
    ...getAuthProviders(environment),
    provideAppInitializer(() => init(inject(ConfigurationService), inject(OneTrustService), inject(EvcAuthService))()),
  ],
};

export function getBaseHref(platformLocation: PlatformLocation): string {
  let baseHref = platformLocation.getBaseHrefFromDOM();

  if (baseHref.endsWith('/')) {
    baseHref = baseHref.slice(0, -1);
  }

  return baseHref;
}

export class CustomTranslateHttpLoader {
  public constructor(
    private http: HttpClient,
    private resources: { prefix; suffix }[],
  ) {}
  public static CustomHttpLoaderFactory(http: HttpClient, resource: { prefix; suffix }[]): CustomTranslateHttpLoader {
    return new CustomTranslateHttpLoader(http, resource);
  }

  public getTranslation(lang) {
    const requests = this.resources.map(resource => {
      const path = resource.prefix + lang + resource.suffix;
      return this.http.get(path).pipe(
        catchError(res => {
          console.error('Something went wrong for the following translation file:', path);
          console.error(res.message);
          if (lang === 'en') {
            return of({});
          } else {
            const defaultPath = resource.prefix + 'en' + resource.suffix;
            return this.http.get(defaultPath).pipe(catchError(result => of({})));
          }
        }),
      );
    });
    return forkJoin(requests).pipe(map(response => all(response)));
  }
}
